import { Box, Typography } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import React, { ReactNode, useReducer } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import zeplinColor from 'theme/Color';

export type FormPageAction = {
  label: string;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  disabled?: boolean;
  loading?: boolean;
  color?: string;
};

export type FormPageLayoutProps = {
  /** The form content — fields, sections, anything */
  children: ReactNode;
  /** Primary CTA (e.g. "Create Client", "Save") */
  primaryAction: FormPageAction;
  /** Secondary actions rendered left of the primary (optional) */
  secondaryActions?: FormPageAction[];
  /** Called when the user confirms they want to discard changes */
  onDiscardConfirm: () => void;
  /** Whether to show an alert banner */
  alertOpen?: boolean;
  alertMessage?: ReactNode;
  alertError?: boolean;
  onAlertClose?: () => void;
  /** Custom background color for the page body */
  pageBg?: string;
  /** Width offset for the fixed footer (default subtracts sidebar width) */
  footerWidthOffset?: string;
};

const FormPageLayout = ({
  children,
  primaryAction,
  secondaryActions = [],
  onDiscardConfirm,
  alertOpen = false,
  alertMessage,
  alertError = false,
  onAlertClose,
  pageBg = zeplinColor.Neutral3,
  footerWidthOffset = '209px',
}: FormPageLayoutProps) => {
  const [modalState, dispatch] = useReducer(modalReducer, { discardChanges: false });

  const handleCancel = () => dispatch({ type: 'OPEN', payload: 'discardChanges' });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',

        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '32px',
          paddingBottom: '100px', // room for fixed footer
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: `calc(100% - ${footerWidthOffset})`,
          zIndex: 100,
          padding: '20px 32px',
          backgroundColor: zeplinColor.Neutral1,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.07)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <CustomButton
          text='Cancel'
          onClick={handleCancel}
          variant='outlined'
          color='customSecondary'
          style={{
            borderRadius: '14px',
            width: '108px',
            border: `1px solid ${zeplinColor.BasicBlack}`,
            '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral1 },
          }}
          textStyle={{ color: zeplinColor.BasicBlack }}
          disabled={false}
        />

        <Box sx={{ display: 'inline-flex', gap: '16px', alignItems: 'center' }}>
          {secondaryActions.map((action, i) => (
            <CustomButton
              key={i}
              text={action.label}
              onClick={action.onClick}
              variant={action.variant ?? 'outlined'}
              color={(action.color ?? 'customSecondary') as any}
              style={{
                borderRadius: '14px',
                minWidth: '120px',
                border: `1px solid ${zeplinColor.BasicBlack}`,
                '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral1 },
                '&.Mui-disabled': {
                  backgroundColor: zeplinColor.Neutral5,
                  color: zeplinColor.BasicBlack,
                },
              }}
              textStyle={{ color: zeplinColor.BasicBlack }}
              disabled={action.disabled ?? false}
            />
          ))}

          <CustomButton
            text={primaryAction.loading ? `${primaryAction.label}…` : primaryAction.label}
            onClick={primaryAction.onClick}
            variant={primaryAction.variant ?? 'contained'}
            style={{
              borderRadius: '14px',
              minWidth: '140px',
              '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              '&.Mui-disabled': {
                backgroundColor: zeplinColor.Neutral5,
              },
            }}
            textStyle={primaryAction.disabled ? { color: zeplinColor.Neutral8 } : undefined}
            disabled={primaryAction.disabled ?? false}
          />
        </Box>
      </Box>

      {alertOpen && (
        <CustomAlert
          open={alertOpen}
          handleClose={onAlertClose ?? (() => {})}
          message={alertMessage}
          error={alertError}
        />
      )}

      <CustomModal
        handleClose={() => dispatch({ type: 'CLOSE', payload: 'discardChanges' })}
        open={modalState.discardChanges}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,
              width: '500px',
              boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 24px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Discard changes?
                </Typography>
                <Typography variant='body2' color={zeplinColor.Neutral9}>
                  You will lose all unsaved changes. Are you sure you want to continue?
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: '16px' }}>
                {/* Keep editing */}
                <Box
                  onClick={() => dispatch({ type: 'CLOSE', payload: 'discardChanges' })}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant='body1' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>

                {/* Discard */}
                <Box
                  onClick={() => {
                    dispatch({ type: 'CLOSE', payload: 'discardChanges' });
                    onDiscardConfirm();
                  }}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Error,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant='body1' color={zeplinColor.Neutral1}>
                      Discard
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default FormPageLayout;
